// src/firebase/userService.js
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './index';
import { UserSchema } from './schemas';

// יצירת משתמש חדש
export const createUser = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const newUser = {
      ...UserSchema,
      ...userData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    await setDoc(userRef, newUser);
    return newUser;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

// קבלת פרטי משתמש
export const getUser = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting user:', error);
    throw error;
  }
};

// עדכון פרטי משתמש
export const updateUser = async (userId, updates) => {
  try {
    const userRef = doc(db, 'users', userId);
    const updateData = {
      ...updates,
      updated_at: new Date().toISOString()
    };
    
    await updateDoc(userRef, updateData);
    return updateData;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// בדיקת הרשאת משתמש לפיצ'ר מסוים
export const checkUserFeature = async (userId, featureName) => {
  try {
    const user = await getUser(userId);
    if (!user) return false;
    
    // בדיקת תוקף המנוי
    if (user.subscription?.status !== 'active') {
      return false;
    }
    
    // בדיקת הפיצ'ר הספציפי
    return user.features?.[featureName] || false;
  } catch (error) {
    console.error('Error checking user feature:', error);
    return false;
  }
};

// הפעלה/השבתה של פיצ'ר
export const toggleUserFeature = async (userId, featureName, enabled) => {
  try {
    const updates = {
      [`features.${featureName}`]: enabled
    };
    
    await updateUser(userId, updates);
    return true;
  } catch (error) {
    console.error('Error toggling feature:', error);
    throw error;
  }
};

// עדכון סטטיסטיקות משתמש
export const updateUserStats = async (userId, statsUpdate) => {
  try {
    const user = await getUser(userId);
    if (!user) throw new Error('User not found');
    
    const currentStats = user.stats || {};
    const newStats = {
      ...currentStats,
      ...statsUpdate,
      // חישוב ממוצעים
      avg_call_duration: currentStats.total_calls > 0 
        ? Math.round(currentStats.total_minutes / currentStats.total_calls)
        : 0
    };
    
    await updateUser(userId, { stats: newStats });
    return newStats;
  } catch (error) {
    console.error('Error updating user stats:', error);
    throw error;
  }
};

// קבלת כל המשתמשים (למנהלים)
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

// חיפוש משתמשים
export const searchUsers = async (searchTerm, searchField = 'email') => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where(searchField, '>=', searchTerm),
      where(searchField, '<=', searchTerm + '\uf8ff')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
};

// עדכון זמן כניסה אחרון
export const updateLastLogin = async (userId) => {
  try {
    await updateUser(userId, {
      last_login: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating last login:', error);
    throw error;
  }
};

export default {
  createUser,
  getUser,
  updateUser,
  checkUserFeature,
  toggleUserFeature,
  updateUserStats,
  getAllUsers,
  searchUsers,
  updateLastLogin
};